import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { adminApi, getAdminToken, type AdminFirm } from '../../lib/admin-api';

const PLAN_STYLE: Record<AdminFirm['plan'], string> = {
  starter: 'bg-zinc-100 text-zinc-700 border-zinc-300',
  essentiel: 'bg-blue-100 text-blue-900 border-blue-300',
  pro: 'bg-emerald-100 text-emerald-900 border-emerald-300',
  enterprise: 'bg-gold/20 text-ink border-gold',
};

export function AdminFirmsPage() {
  const navigate = useNavigate();
  const [firms, setFirms] = useState<AdminFirm[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: '',
    legal_name: '',
    matricule_fiscal: '',
    plan: 'starter' as AdminFirm['plan'],
  });

  useEffect(() => {
    if (!getAdminToken()) {
      navigate('/admin/login', { replace: true });
      return;
    }
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function refresh() {
    setError(null);
    try {
      const r = await adminApi.listFirms();
      setFirms(r.firms);
    } catch (e) {
      setError((e as Error).message);
    }
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await adminApi.createFirm(form);
      setShowCreate(false);
      setForm({ name: '', legal_name: '', matricule_fiscal: '', plan: 'starter' });
      await refresh();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-4 max-w-5xl">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <Link to="/admin/settings" className="text-taupe text-sm hover:text-ink">← Settings</Link>
          <h2 className="font-serif text-3xl font-bold mt-2">Cabinets</h2>
          <p className="text-taupe text-sm">Liste des cabinets clients de la plateforme.</p>
        </div>
        <button type="button" onClick={() => setShowCreate(true)} className="btn-primary">
          + Créer un cabinet
        </button>
      </div>

      {error && <div className="card border border-red-300 bg-red-50 text-red-900 text-sm">{error}</div>}

      {firms === null && !error && <div className="text-taupe text-sm">Chargement…</div>}

      {firms !== null && firms.length === 0 && (
        <div className="card text-center py-12 text-taupe">Aucun cabinet enregistré.</div>
      )}

      {firms !== null && firms.length > 0 && (
        <div className="card !p-0 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-cream/40 text-xs uppercase tracking-wider text-taupe border-b border-border">
                <th className="px-4 py-2 text-left">ID</th>
                <th className="px-4 py-2 text-left">Nom</th>
                <th className="px-4 py-2 text-left">Matricule</th>
                <th className="px-4 py-2 text-left">Plan</th>
                <th className="px-4 py-2 text-left">Créé</th>
              </tr>
            </thead>
            <tbody>
              {firms.map((f) => (
                <tr key={f.id} className="border-b border-border/50 hover:bg-cream/30">
                  <td className="px-4 py-2 font-mono text-xs">{f.id.slice(-12)}</td>
                  <td className="px-4 py-2">
                    <div className="font-medium">{f.name}</div>
                    <div className="text-xs text-taupe">{f.legal_name}</div>
                  </td>
                  <td className="px-4 py-2 font-mono text-xs">{f.matricule_fiscal}</td>
                  <td className="px-4 py-2">
                    <span className={`text-xs px-2 py-0.5 rounded border ${PLAN_STYLE[f.plan]}`}>{f.plan}</span>
                  </td>
                  <td className="px-4 py-2 text-xs text-taupe">{f.created_at ? formatDate(f.created_at) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showCreate && (
        <div className="fixed inset-0 bg-ink/60 flex items-center justify-center p-4 z-50">
          <form onSubmit={handleCreate} className="bg-cream rounded-lg shadow-xl w-full max-w-md">
            <div className="p-5 border-b border-border">
              <div className="text-xs uppercase tracking-wider text-taupe">Nouveau cabinet</div>
              <h3 className="font-serif text-xl font-bold">Création</h3>
            </div>
            <div className="p-5 space-y-3 text-sm">
              <label className="block">
                <span className="font-medium">Nom commercial</span>
                <input
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full border border-border rounded px-3 py-2 mt-1"
                />
              </label>
              <label className="block">
                <span className="font-medium">Raison sociale</span>
                <input
                  required
                  value={form.legal_name}
                  onChange={(e) => setForm({ ...form, legal_name: e.target.value })}
                  className="w-full border border-border rounded px-3 py-2 mt-1"
                />
              </label>
              <label className="block">
                <span className="font-medium">Matricule fiscal</span>
                <input
                  required
                  pattern="\d{7}[A-Z]/[A-Z]/[A-Z]/\d{3}"
                  value={form.matricule_fiscal}
                  onChange={(e) => setForm({ ...form, matricule_fiscal: e.target.value.toUpperCase() })}
                  className="w-full border border-border rounded px-3 py-2 mt-1 font-mono"
                  placeholder="1234567A/M/A/000"
                />
              </label>
              <label className="block">
                <span className="font-medium">Plan</span>
                <select
                  value={form.plan}
                  onChange={(e) => setForm({ ...form, plan: e.target.value as AdminFirm['plan'] })}
                  className="w-full border border-border rounded px-3 py-2 mt-1"
                >
                  <option value="starter">Starter</option>
                  <option value="essentiel">Essentiel</option>
                  <option value="pro">Pro</option>
                  <option value="enterprise">Enterprise</option>
                </select>
              </label>
            </div>
            <div className="p-5 border-t border-border flex justify-end gap-2">
              <button type="button" onClick={() => setShowCreate(false)} className="btn-secondary">
                Annuler
              </button>
              <button type="submit" disabled={saving} className="btn-primary">
                {saving ? 'Création…' : 'Créer'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('fr-FR');
  } catch {
    return iso;
  }
}
